import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { absoluteUrl } from "../components/ImageUpload";
import { useAuth } from "../context/AuthContext";

/**
 * Print-ready A4 «امسح واطلب» poster.
 * Two entry points:
 *   /dashboard/qr-poster  (owner uses their own restaurant)
 *   /admin/qr-poster?slug=xxx  (super admin passes slug)
 */
export default function QRPoster() {
  const { user } = useAuth();
  const nav = useNavigate();
  const params = useParams();
  const [sp] = useSearchParams();
  const slug = params.slug || sp.get("slug");
  const branchId = sp.get("branch_id");
  const [r, setR] = useState(null);
  const handlePrint = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const originalTitle = document.title;
    try {
      document.title = `Sticker - ${r.name}`;
      window.print();
    } finally {
      document.title = originalTitle;
    }
  };

  useEffect(() => {
    if (slug) {
      api.get(`/public/restaurants/${slug}`, { params: { branch_id: branchId } })
        .then((response) => setR(response.data.restaurant))
        .catch(() => {});
    }
    else api.get("/restaurants/mine").then(x => setR(x.data)).catch(() => {});
  }, [branchId, slug]);

  if (!r) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;
  const branchPath = r.branch_code ? `/${encodeURIComponent(r.branch_code)}` : "";
  const url = `${window.location.origin}/r/${r.slug}${branchPath}`;

  return (
    <div className="min-h-screen bg-[#F3EFE7] print:bg-white">
      {/* Screen controls (hidden when printing) */}
      <div className="print:hidden">
        <header className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <button onClick={() => nav(-1)} className="w-10 h-10 rounded-xl bg-white grid place-items-center" data-testid="qr-back"><ArrowLeft size={18} className="rotate-180"/></button>
          <button
            type="button"
            onClick={handlePrint}
            className="pill brand-grad text-white px-5 py-2.5 font-bold inline-flex items-center gap-2"
            data-testid="qr-print"
          >
            <Printer size={16}/> طباعة الملصق
          </button>
        </header>
        <div className="max-w-4xl mx-auto px-5 pb-4 text-xs text-gray-500 text-center">
          مقاس A4. اطبع الملصق وضعه عند الكاشير أو مدخل المحل ليتمكن عملاؤك من الطلب بسهولة.
        </div>
      </div>

      {/* A4 poster */}
      <div className="mx-auto bg-white shadow-xl print:shadow-none flex flex-col items-center justify-between text-center overflow-hidden a4-sheet">
        <div className="w-full h-8 brand-grad" />

        <div className="flex-1 flex flex-col items-center justify-center px-8 py-6">
          <div className="mt-8">
            {r.logo_url ? (
              r.logo_url.length <= 4 ?
                <div className="text-7xl">{r.logo_url}</div> :
                <img src={absoluteUrl(r.logo_url)} alt="" className="w-28 h-28 rounded-3xl object-cover mx-auto card-shadow" />
            ) : (
              <div className="w-24 h-24 rounded-3xl brand-grad text-white grid place-items-center text-4xl mx-auto">🍽️</div>
            )}
          </div>
          <h1
            className="mt-4 text-5xl font-extrabold text-gray-900"
            data-testid="poster-name"
          >
            {r.branch_name || r.name}
          </h1>

          <div className="mt-6 text-3xl font-extrabold text-orange-600">امسح واطلب 📱</div>
          <div className="mt-1 text-gray-700 text-lg">اطلب من جوالك واستلم عند سيارتك</div>

          <div className="mt-8 p-5 rounded-3xl bg-white border-4 border-orange-500 inline-block">
            <QRCodeSVG value={url} size={280} level="H" bgColor="#ffffff" fgColor="#111827" data-testid="poster-qr"/>
          </div>

          <div className="mt-6 text-sm text-gray-500 num-badge break-all max-w-full px-4">{url}</div>

          <ol className="mt-6 grid grid-cols-3 gap-3 max-w-xl w-full">
            {[
              ["1", "امسح الرمز"],
              ["2", "اطلب من جوالك"],
              ["3", "استلم عند سيارتك"],
            ].map(([n, t]) => (
              <li key={n} className="rounded-2xl bg-orange-50 border border-orange-200 p-3">
                <div className="w-9 h-9 mx-auto rounded-xl brand-grad text-white grid place-items-center font-extrabold">{n}</div>
                <div className="mt-2 font-bold text-gray-900">{t}</div>
              </li>
            ))}
          </ol>
        </div>

        <div className="w-full brand-grad text-white text-sm font-bold py-3">
          مدعوم بواسطة خِفّة
        </div>
      </div>

      <style>{`
        .a4-sheet {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white; }
          .a4-sheet { box-shadow: none; margin: 0; }
        }
      `}</style>
    </div>
  );
}
